import { createEntityId } from '@/core/EntityId';
import type { FloraInstance, Vec3 } from '@/flora/FloraInstance';
import type { FloraWorld } from '@/flora/FloraWorld';
import { duplicateInstances } from './DuplicateTool';

/** A named collection of plants that moves and duplicates as a unit (spec §4.6). */
export interface PlantGroup {
  readonly id: string;
  name: string;
  readonly memberIds: string[];
}

/** Result of duplicating a group: the new group plus its freshly created member instances. */
export interface GroupDuplication {
  readonly group: PlantGroup;
  readonly instances: FloraInstance[];
}

/**
 * Manages named plant groups over a {@link FloraWorld}. Moving or duplicating a group applies the
 * same delta/offset to every member, so relative spacing is preserved.
 */
export class GroupManager {
  private readonly groups = new Map<string, PlantGroup>();

  constructor(
    private readonly world: FloraWorld,
    private readonly nextId: () => string = () => createEntityId('group-'),
  ) {}

  /** Create a group from the given member ids. Returns undefined if fewer than one exists. */
  create(memberIds: string[], name?: string): PlantGroup | undefined {
    const valid = memberIds.filter((id) => this.world.has(id));
    if (valid.length === 0) return undefined;
    const id = this.nextId();
    const group: PlantGroup = { id, name: name ?? `Group ${this.groups.size + 1}`, memberIds: valid };
    this.groups.set(id, group);
    return group;
  }

  get(id: string): PlantGroup | undefined {
    return this.groups.get(id);
  }

  all(): PlantGroup[] {
    return [...this.groups.values()];
  }

  delete(id: string): boolean {
    return this.groups.delete(id);
  }

  /** Forget every group (used when the site is replaced). */
  clear(): void {
    this.groups.clear();
  }

  /**
   * Restore groups from a loaded project, keeping their original ids so the document round-trips.
   * Members whose plants are missing are dropped rather than left dangling.
   */
  load(groups: readonly PlantGroup[]): void {
    this.groups.clear();
    for (const group of groups) {
      const memberIds = group.memberIds.filter((id) => this.world.has(id));
      if (memberIds.length === 0) continue;
      this.groups.set(group.id, { id: group.id, name: group.name, memberIds });
    }
  }

  /** Translate every member of the group by the same delta, preserving relative spacing. */
  moveGroup(id: string, delta: Vec3): void {
    const group = this.groups.get(id);
    if (group === undefined) return;
    for (const memberId of group.memberIds) {
      const inst = this.world.get(memberId);
      if (inst === undefined) continue;
      this.world.update({
        ...inst,
        position: {
          x: inst.position.x + delta.x,
          y: inst.position.y + delta.y,
          z: inst.position.z + delta.z,
        },
      });
    }
  }

  /**
   * Duplicate the group: every member is copied with the same offset (preserving relative spacing),
   * added to the world, and collected into a new group.
   */
  duplicateGroup(id: string, offset: Vec3 = { x: 1, y: 0, z: 1 }): GroupDuplication | undefined {
    const group = this.groups.get(id);
    if (group === undefined) return undefined;

    const sources = group.memberIds
      .map((memberId) => this.world.get(memberId))
      .filter((inst): inst is FloraInstance => inst !== undefined);
    const clones = duplicateInstances(sources, offset);
    for (const clone of clones) this.world.add(clone);

    const newGroup: PlantGroup = {
      id: this.nextId(),
      name: `${group.name} copy`,
      memberIds: clones.map((clone) => clone.id),
    };
    this.groups.set(newGroup.id, newGroup);
    return { group: newGroup, instances: clones };
  }
}
